import { useState } from 'react';
import { IconFile, IconPlus } from '../components/icons';
import { RowActions } from '../components/RowActions';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Toggle, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDate } from '../lib/format';
import { UTILITY_KIND_LABELS, UTILITY_KINDS, type UtilityKind } from '../lib/utilityKinds';

/**
 * Versorgungstarife (Objekt-Check, Kap. 7).
 *
 * Jeder Tarif hängt an genau einem Versorger und einer Sparte – ein Zähler
 * darf nur mit einem Tarif verknüpft werden, dessen Sparte zu seiner eigenen
 * passt (durchgesetzt beim Anlegen des Zählers, nicht hier).
 */
export function UtilityTariffsPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [modal, setModal] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const suppliers = useAsync(() => api.get('/api/utilities/suppliers'), []);
  const tariffs = useAsync(
    () => api.get('/api/utilities/tariffs', { includeInactive: showInactive ? 'true' : '' }),
    [showInactive],
  );

  const mayManage = can('utilities.manage');
  const list: any[] = tariffs.data ?? [];

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconFile width={22} height={22} />}
        title="Versorgungstarife"
        subtitle="Vertragstarife je Versorger und Sparte – Grund- und laufende Kosten, Laufzeit und Ansprechpartner."
        pill="Geschäftspartner"
        tone="info"
        actions={
          mayManage ? (
            <button
              className="btn primary"
              disabled={!(suppliers.data ?? []).length}
              title={!(suppliers.data ?? []).length ? 'Zuerst einen Versorger anlegen.' : undefined}
              onClick={() => setModal('neu')}
            >
              <IconPlus width={15} height={15} /> Tarif anlegen
            </button>
          ) : undefined
        }
      />

      <Card noBody>
        <div className="card-body tight">
          <label className="checkbox-row">
            <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
            Stillgelegte anzeigen
          </label>
        </div>
      </Card>

      {tariffs.loading && !tariffs.data ? (
        <Loading />
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconFile width={20} height={20} />}
            title="Kein Versorgungstarif hinterlegt"
            hint={!(suppliers.data ?? []).length ? 'Dazu zuerst einen Versorger anlegen.' : undefined}
          />
        </Card>
      ) : (
        <Card noBody>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Tarif</th>
                  <th>Versorger</th>
                  <th>Sparte</th>
                  <th className="num">Grundkosten</th>
                  <th className="num">Laufende Kosten</th>
                  <th>Laufzeit</th>
                  {mayManage && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((tariff: any) => (
                  <tr key={tariff.id} style={tariff.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{tariff.name}</strong>
                      {tariff.contract_number && <div className="small subtle">{tariff.contract_number}</div>}
                    </td>
                    <td className="small subtle">{tariff.supplier_name}</td>
                    <td>
                      <Badge>{UTILITY_KIND_LABELS[tariff.kind as UtilityKind] ?? tariff.kind}</Badge>
                    </td>
                    <td className="num small">{tariff.base_cost != null ? formatCurrency(tariff.base_cost) : '–'}</td>
                    <td className="num small">{tariff.unit_cost != null ? formatCurrency(tariff.unit_cost) : '–'}</td>
                    <td className="small subtle">
                      {tariff.term_start || tariff.term_end
                        ? `${tariff.term_start ? formatDate(tariff.term_start) : '–'} – ${tariff.term_end ? formatDate(tariff.term_end) : '–'}`
                        : '–'}
                    </td>
                    {mayManage && (
                      <td className="actions">
                        {tariff.is_active ? (
                          <RowActions
                            label={tariff.name}
                            onEdit={() => setModal(`bearbeiten-${tariff.id}`)}
                            deletePath={`/api/utilities/tariffs/${tariff.id}${
                              tariff.meter_count > 0 ? '' : '?permanent=true'
                            }`}
                            confirmText={
                              tariff.meter_count > 0
                                ? `An „${tariff.name}“ hängen ${tariff.meter_count} Zähler. Der Tarif wird stillgelegt und bleibt als Beleg erhalten. Fortfahren?`
                                : `Tarif „${tariff.name}“ löschen?`
                            }
                            onDeleted={() => tariffs.reload()}
                          />
                        ) : (
                          <button
                            className="btn sm"
                            onClick={async () => {
                              await api.post(`/api/utilities/tariffs/${tariff.id}/reactivate`);
                              notify('Tarif wieder aktiv.', 'success');
                              tariffs.reload();
                            }}
                          >
                            Reaktivieren
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(modal === 'neu' || modal?.startsWith('bearbeiten-')) && (
        <TariffModal
          suppliers={suppliers.data ?? []}
          tariff={list.find((entry: any) => String(entry.id) === modal?.replace('bearbeiten-', ''))}
          onClose={() => setModal(null)}
          onSaved={() => {
            const wasNew = modal === 'neu';
            setModal(null);
            tariffs.reload();
            notify(wasNew ? 'Tarif angelegt.' : 'Tarif gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function TariffModal({
  suppliers,
  tariff,
  onClose,
  onSaved,
}: {
  suppliers: any[];
  tariff?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    supplierId: tariff?.supplier_id ? String(tariff.supplier_id) : '',
    kind: (tariff?.kind ?? 'electricity') as UtilityKind,
    name: tariff?.name ?? '',
    contractNumber: tariff?.contract_number ?? '',
    baseCost: tariff?.base_cost != null ? String(tariff.base_cost) : '',
    unitCost: tariff?.unit_cost != null ? String(tariff.unit_cost) : '',
    termStart: tariff?.term_start?.slice(0, 10) ?? '',
    termEnd: tariff?.term_end?.slice(0, 10) ?? '',
    contactName: tariff?.contact_name ?? '',
    note: tariff?.note ?? '',
    isActive: tariff ? !!tariff.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title={tariff ? `Tarif bearbeiten – ${tariff.name}` : 'Tarif anlegen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.supplierId || form.name.trim().length < 2}
            onClick={async () => {
              try {
                const payload = {
                  ...form,
                  supplierId: Number(form.supplierId),
                  contractNumber: form.contractNumber || null,
                  baseCost: form.baseCost ? Number(form.baseCost) : null,
                  unitCost: form.unitCost ? Number(form.unitCost) : null,
                  termStart: form.termStart || null,
                  termEnd: form.termEnd || null,
                  contactName: form.contactName || null,
                  note: form.note || null,
                };
                if (tariff) await api.patch(`/api/utilities/tariffs/${tariff.id}`, payload);
                else await api.post('/api/utilities/tariffs', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Versorger" required>
            <SearchSelect
              value={form.supplierId}
              onChange={(value) => set('supplierId', value)}
              options={suppliers.map((entry: any) => ({ value: String(entry.id), label: entry.name }))}
              placeholder="– bitte wählen –"
            />
          </Field>
          <Field label="Sparte" required>
            <select value={form.kind} onChange={(event) => set('kind', event.target.value)}>
              {UTILITY_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {UTILITY_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tarifbezeichnung" required>
            <input value={form.name} autoFocus onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field label="Vertragsnummer">
            <input value={form.contractNumber} onChange={(event) => set('contractNumber', event.target.value)} />
          </Field>
          <Field label="Grundkosten (€)">
            <input type="number" min="0" step="0.01" value={form.baseCost} onChange={(event) => set('baseCost', event.target.value)} />
          </Field>
          <Field label="Laufende Kosten (€ je Einheit)">
            <input type="number" min="0" step="0.0001" value={form.unitCost} onChange={(event) => set('unitCost', event.target.value)} />
          </Field>
          <Field label="Vertragsbeginn">
            <input type="date" value={form.termStart} onChange={(event) => set('termStart', event.target.value)} />
          </Field>
          <Field label="Vertragsende">
            <input type="date" value={form.termEnd} onChange={(event) => set('termEnd', event.target.value)} />
          </Field>
          <Field label="Ansprechpartner">
            <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          </Field>
        </div>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>
        <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
      </div>
    </Modal>
  );
}
